/**
 * Fase 2 (W9, F1d): HTML de e-mail é DADO. O sanitizador reconstrói o documento a partir de uma lista fechada de
 * etiquetas e atributos (nada passa cru): texto e valores de atributo saem escapados, script, estilo em bloco, quadro,
 * objeto, formulário, SVG e MathML somem com o conteúdo, `on*` nunca entra, link só http(s), mailto e tel, e imagem
 * remota perde o `src` (vai para `data-src-remota`; a tela só devolve com o clique em "mostrar imagens"). Roda no
 * servidor (na entrada do e-mail) e no navegador (antes de montar o quadro isolado), sem depender de DOM.
 */

const PERMITIDAS = new Set([
  'a', 'abbr', 'address', 'article', 'b', 'big', 'blockquote', 'br', 'caption', 'center', 'cite', 'code', 'col', 'colgroup', 'dd', 'del', 'div', 'dl', 'dt', 'em',
  'figcaption', 'figure', 'font', 'footer', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hr', 'i', 'img', 'ins', 'kbd', 'li', 'main', 'mark', 'ol', 'p', 'pre', 'q',
  's', 'section', 'small', 'span', 'strike', 'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'tt', 'u', 'ul', 'wbr',
]);
const VAZIAS = new Set(['br', 'hr', 'img', 'col', 'wbr']);
/** Somem com todo o conteúdo (o texto de dentro não é texto do e-mail, ou não dá para analisar com segurança). */
const DESCARTAR_COM_CONTEUDO = new Set(['script', 'style', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet', 'noscript', 'noembed', 'noframes', 'template', 'title', 'head', 'svg', 'math', 'xmp', 'plaintext', 'textarea', 'select', 'video', 'audio', 'canvas']);
const ATRIBUTOS = new Set(['style', 'align', 'valign', 'width', 'height', 'bgcolor', 'color', 'border', 'cellpadding', 'cellspacing', 'colspan', 'rowspan', 'dir', 'title', 'alt', 'lang', 'face', 'size', 'start', 'type', 'span', 'nowrap']);
const ESQUEMAS_LINK = /^(https?:|mailto:|tel:)/;
const IMAGEM_DADOS = /^data:image\/(png|jpeg|gif|webp);base64,[a-z0-9+/=\s]+$/i;
const LIMITE_ENTRADA = 512 * 1024;
const LIMITE_IMAGEM_DADOS = 200 * 1024;
/** Declaração de estilo que pode buscar recurso externo ou executar algo: sai inteira. */
const ESTILO_PERIGOSO = /url\s*\(|expression\s*\(|@import|javascript:|vbscript:|behavior\s*:|-moz-binding|\\|image-set\s*\(|src\s*\(/i;

export interface HtmlSeguro {
  html: string;
  /** Quantas imagens remotas foram bloqueadas (a tela mostra "mostrar imagens" só quando há alguma). */
  imagensRemotas: number;
}

const NOMEADAS: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: '\u00a0', colon: ':', tab: '\t', newline: '\n', sol: '/', lpar: '(', rpar: ')' };

/** Decodifica as entidades de um valor de atributo ou texto (numéricas e as nomeadas que importam para a conferência). */
export function decodificarEntidades(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);?/gi, (m, c: string) => {
    if (c[0] === '#') {
      const n = c[1] === 'x' || c[1] === 'X' ? parseInt(c.slice(2), 16) : parseInt(c.slice(1), 10);
      return Number.isFinite(n) && n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : '';
    }
    return NOMEADAS[c.toLowerCase()] ?? m;
  });
}

const escaparTexto = (s: string) => s.replace(/&(?!(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]{1,31});)/gi, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escaparAtributo = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
// eslint-disable-next-line no-control-regex
const semControle = (s: string) => s.replace(/[\u0000- \u007f-\u009f]/g, '');

function estiloSeguro(v: string): string | null {
  const decl = decodificarEntidades(v)
    .split(';')
    .map((d) => d.trim())
    .filter((d) => d && /^[a-z-]+\s*:/i.test(d) && !ESTILO_PERIGOSO.test(d));
  return decl.length ? decl.join('; ') : null;
}

interface Etiqueta {
  nome: string;
  fechamento: boolean;
  atributos: [string, string][];
  /** Índice logo depois do `>`. */
  fim: number;
}

/**
 * Lê uma etiqueta a partir de `<` em `i`. Devolve null se não for etiqueta (o `<` vira texto) e `FIM` quando a leitura
 * chegou ao fim do texto sem fechar a etiqueta (sem `>` ou aspa sem par): nesse caso nenhum `<` adiante fecha etiqueta pelo
 * mesmo caminho, e o resto vira texto de uma vez. Assim cada caractere é lido no máximo uma vez por chamada que avança, e o
 * sanitizador fica linear (rcrm4 SEG-A1 e ROB-A1: antes, cada `<` sem `>` revarria o resto do texto, tempo quadrático).
 */
const FIM = 'fim' as const;
const NOME_ETIQUETA = /[a-zA-Z][a-zA-Z0-9:-]{0,63}/y;
const NOME_ATRIBUTO = /[^\s/>="'<]{1,256}/y;
const VALOR_SEM_ASPAS = /[^\s>]*/y;
function lerEtiqueta(s: string, i: number): Etiqueta | null | typeof FIM {
  let p = i + 1;
  const fechamento = s[p] === '/';
  if (fechamento) p++;
  NOME_ETIQUETA.lastIndex = p;
  const m = NOME_ETIQUETA.exec(s);
  if (!m) return null;
  const nome = m[0].toLowerCase();
  p += m[0].length;
  const atributos: [string, string][] = [];
  while (p < s.length) {
    while (p < s.length && /[\s/]/.test(s[p]!)) p++;
    if (p >= s.length) return FIM;
    if (s[p] === '>') return { nome, fechamento, atributos, fim: p + 1 };
    NOME_ATRIBUTO.lastIndex = p;
    const an = NOME_ATRIBUTO.exec(s);
    if (!an) {
      p++;
      continue;
    }
    const nomeAttr = an[0].toLowerCase();
    p += an[0].length;
    while (p < s.length && /\s/.test(s[p]!)) p++;
    let valor = '';
    if (s[p] === '=') {
      p++;
      while (p < s.length && /\s/.test(s[p]!)) p++;
      const q = s[p];
      if (q === '"' || q === "'") {
        const fimValor = s.indexOf(q, p + 1);
        if (fimValor < 0) return FIM;
        valor = s.slice(p + 1, fimValor);
        p = fimValor + 1;
      } else {
        VALOR_SEM_ASPAS.lastIndex = p;
        const v = VALOR_SEM_ASPAS.exec(s)![0];
        valor = v;
        p += v.length;
      }
    }
    if (atributos.length < 40) atributos.push([nomeAttr, valor]);
  }
  return FIM;
}

/** Posição logo depois do fechamento de `nome` (contando aninhamento do mesmo nome) ou o fim do texto. */
function pularConteudo(s: string, desde: number, nome: string): number {
  const re = new RegExp(`<(/?)${nome}(?=[\\s/>])[^<>]*>`, 'gi');
  re.lastIndex = desde;
  let nivel = 1;
  for (let m = re.exec(s); m; m = re.exec(s)) {
    nivel += m[1] ? -1 : 1;
    if (nivel === 0) return re.lastIndex;
  }
  return s.length;
}

function atributosSeguros(nome: string, attrs: [string, string][], cont: { remotas: number }): string {
  const saida: string[] = [];
  for (const [a, bruto] of attrs) {
    if (a === 'href' && nome === 'a') {
      const url = semControle(decodificarEntidades(bruto)).toLowerCase();
      if (ESQUEMAS_LINK.test(url)) saida.push(`href="${escaparAtributo(decodificarEntidades(bruto).trim())}"`);
      continue;
    }
    if (a === 'src' && nome === 'img') {
      const original = decodificarEntidades(bruto).trim();
      const url = semControle(original).toLowerCase();
      if (/^https?:/.test(url) || url.startsWith('//')) {
        cont.remotas++;
        saida.push(`data-src-remota="${escaparAtributo(url.startsWith('//') ? `https:${original}` : original)}"`);
      } else if (IMAGEM_DADOS.test(original) && original.length <= LIMITE_IMAGEM_DADOS) {
        saida.push(`src="${escaparAtributo(original.replace(/\s/g, ''))}"`);
      }
      continue;
    }
    // Segunda passada (o navegador sanitiza de novo o que o servidor guardou): a imagem já bloqueada continua bloqueada.
    if (a === 'data-src-remota' && nome === 'img') {
      const url = decodificarEntidades(bruto).trim();
      if (/^https?:/i.test(semControle(url))) {
        cont.remotas++;
        saida.push(`data-src-remota="${escaparAtributo(url)}"`);
      }
      continue;
    }
    if (!ATRIBUTOS.has(a)) continue;
    if (a === 'style') {
      const e = estiloSeguro(bruto);
      if (e) saida.push(`style="${escaparAtributo(e)}"`);
      continue;
    }
    const v = decodificarEntidades(bruto);
    if (/javascript:|vbscript:|expression\s*\(/i.test(semControle(v))) continue;
    saida.push(`${a}="${escaparAtributo(v.slice(0, 500))}"`);
  }
  if (nome === 'a' && saida.some((x) => x.startsWith('href='))) saida.push('target="_blank"', 'rel="noopener noreferrer nofollow"');
  return saida.length ? ` ${saida.join(' ')}` : '';
}

/** Sanitiza o HTML de um e-mail. Ver o comentário do arquivo. */
export function sanitizarHtmlEmail(entrada: string): HtmlSeguro {
  const s = String(entrada ?? '').slice(0, LIMITE_ENTRADA);
  const cont = { remotas: 0 };
  const out: string[] = [];
  let i = 0;
  while (i < s.length) {
    const lt = s.indexOf('<', i);
    if (lt < 0) {
      out.push(escaparTexto(s.slice(i)));
      break;
    }
    if (lt > i) out.push(escaparTexto(s.slice(i, lt)));
    // Comentário, CDATA, doctype e instrução de processamento: somem.
    if (s.startsWith('<!--', lt)) {
      const f = s.indexOf('-->', lt + 4);
      i = f < 0 ? s.length : f + 3;
      continue;
    }
    if (s[lt + 1] === '!' || s[lt + 1] === '?') {
      const f = s.indexOf('>', lt);
      i = f < 0 ? s.length : f + 1;
      continue;
    }
    const e = lerEtiqueta(s, lt);
    if (e === FIM) {
      out.push(escaparTexto(s.slice(lt)));
      break;
    }
    if (!e) {
      out.push('&lt;');
      i = lt + 1;
      continue;
    }
    i = e.fim;
    if (DESCARTAR_COM_CONTEUDO.has(e.nome)) {
      if (!e.fechamento) i = pularConteudo(s, e.fim, e.nome);
      continue;
    }
    if (!PERMITIDAS.has(e.nome)) continue;
    if (e.fechamento) {
      if (!VAZIAS.has(e.nome)) out.push(`</${e.nome}>`);
      continue;
    }
    out.push(`<${e.nome}${atributosSeguros(e.nome, e.atributos, cont)}>`);
  }
  return { html: out.join(''), imagensRemotas: cont.remotas };
}

/** Devolve o `src` das imagens remotas bloqueadas (o clique em "mostrar imagens"); só mexe no atributo que o sanitizador gerou. */
export function liberarImagensRemotas(htmlSeguro: string): string {
  return htmlSeguro.replace(/<img([^>]*?) data-src-remota="([^"]*)"/g, (_m, antes: string, url: string) => (/^https?:/i.test(url.replace(/&amp;/g, '&')) ? `<img${antes} src="${url}"` : `<img${antes}`));
}

/**
 * Tira script, estilo, cabeçalho, título, SVG e MathML com o conteúdo. O bloco sem fechamento fica (a etiqueta sai depois,
 * o texto fica), como antes; o nome que já não achou fechamento não é procurado de novo (nenhum adiante acharia): linear.
 */
function semBlocosOcultos(s: string): string {
  const abre = /<(script|style|head|title|svg|math)/gi;
  const semFechamento = new Set<string>();
  const out: string[] = [];
  let i = 0;
  for (let m = abre.exec(s); m; m = abre.exec(s)) {
    const nome = m[1]!.toLowerCase();
    if (semFechamento.has(nome)) continue;
    const fecha = new RegExp(`<\\/${nome}\\s*>`, 'gi');
    fecha.lastIndex = abre.lastIndex;
    if (!fecha.exec(s)) {
      semFechamento.add(nome);
      continue;
    }
    out.push(s.slice(i, m.index), ' ');
    i = fecha.lastIndex;
    abre.lastIndex = i;
  }
  out.push(s.slice(i));
  return out.join('');
}

/** Tira os comentários (o que abre e não fecha fica, como antes); uma busca por comentário: linear. */
function semComentarios(s: string): string {
  const out: string[] = [];
  let i = 0;
  for (let a = s.indexOf('<!--', i); a >= 0; a = s.indexOf('<!--', i)) {
    const f = s.indexOf('-->', a + 4);
    if (f < 0) break;
    out.push(s.slice(i, a));
    i = f + 3;
  }
  out.push(s.slice(i));
  return out.join('');
}

/** Texto simples de um HTML (prévia da conversa quando o e-mail não tem parte text/plain). */
export function textoDeHtml(html: string): string {
  const s = String(html ?? '').slice(0, LIMITE_ENTRADA);
  const semBlocos = semBlocosOcultos(s);
  const comQuebras = semBlocos.replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|tr|li|h[1-6]|table|blockquote)\s*>/gi, '\n');
  // `[^<>]` em vez de `[^>]`: sem `>` adiante, cada `<` para no próximo `<` e o texto não é revarrido (linear; rcrm4 SEG-A1).
  return decodificarEntidades(semComentarios(comQuebras).replace(/<[^<>]*>/g, ''))
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t\f\v]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ---------------------------------------------------------------------------------------------------------------- texto rico

/**
 * Lote crm4 (L8 e L9, F4 e F8): HTML do EDITOR com formatação (corpo do e-mail da negociação, modelos de e-mail e os blocos
 * da proposta). Lista ainda mais fechada que a do e-mail recebido: só parágrafo, quebra, negrito, itálico, sublinhado,
 * riscado, listas, citação e link (http, https, mailto, tel). Nenhum atributo além do `href` do link e do `text-align`
 * (esquerda, centro, direita, justificado) no estilo; imagem, tabela, cor, fonte e qualquer outro estilo saem. Script, estilo
 * em bloco e as demais etiquetas perigosas somem com o conteúdo (mesmo leitor do e-mail). As etiquetas saem EQUILIBRADAS: o
 * fechamento sem abertura some e o que ficou aberto fecha no fim, para o trecho não quebrar a moldura do e-mail nem a página.
 * Roda no servidor (antes de gravar e de enviar) e de novo no navegador (antes de pôr na tela).
 */
const RICO_PERMITIDAS = new Set(['p', 'br', 'div', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'ul', 'ol', 'li', 'a', 'blockquote', 'span']);
const RICO_ALINHAMENTO = /^\s*text-align\s*:\s*(left|center|right|justify)\s*$/i;
/** Normaliza para a forma curta: `strong`, `em`, `s`. */
const RICO_TROCA: Record<string, string> = { b: 'strong', i: 'em', strike: 's' };

function atributosRicos(nome: string, attrs: [string, string][]): string {
  const saida: string[] = [];
  for (const [a, bruto] of attrs) {
    if (a === 'href' && nome === 'a') {
      const v = decodificarEntidades(bruto).trim();
      if (ESQUEMAS_LINK.test(semControle(v).toLowerCase()) && v.length <= 2000) saida.push(`href="${escaparAtributo(v)}"`);
      continue;
    }
    if (a === 'style' && nome !== 'a' && nome !== 'br') {
      const alinhamento = decodificarEntidades(bruto)
        .split(';')
        .map((d) => RICO_ALINHAMENTO.exec(d)?.[1]?.toLowerCase())
        .find(Boolean);
      if (alinhamento) saida.push(`style="text-align: ${alinhamento}"`);
    }
  }
  if (nome === 'a' && saida.length) saida.push('target="_blank"', 'rel="noopener noreferrer nofollow"');
  return saida.length ? ` ${saida.join(' ')}` : '';
}

/** Sanitiza o HTML do editor com formatação. Ver o comentário acima. Vazio (só espaço e quebras) devolve ''. */
export function sanitizarHtmlRico(entrada: string): string {
  const s = String(entrada ?? '').slice(0, LIMITE_ENTRADA);
  const out: string[] = [];
  const abertas: string[] = [];
  let i = 0;
  while (i < s.length) {
    const lt = s.indexOf('<', i);
    if (lt < 0) {
      out.push(escaparTexto(s.slice(i)));
      break;
    }
    if (lt > i) out.push(escaparTexto(s.slice(i, lt)));
    if (s.startsWith('<!--', lt)) {
      const f = s.indexOf('-->', lt + 4);
      i = f < 0 ? s.length : f + 3;
      continue;
    }
    if (s[lt + 1] === '!' || s[lt + 1] === '?') {
      const f = s.indexOf('>', lt);
      i = f < 0 ? s.length : f + 1;
      continue;
    }
    const e = lerEtiqueta(s, lt);
    if (e === FIM) {
      out.push(escaparTexto(s.slice(lt)));
      break;
    }
    if (!e) {
      out.push('&lt;');
      i = lt + 1;
      continue;
    }
    i = e.fim;
    if (DESCARTAR_COM_CONTEUDO.has(e.nome)) {
      if (!e.fechamento) i = pularConteudo(s, e.fim, e.nome);
      continue;
    }
    if (!RICO_PERMITIDAS.has(e.nome)) continue;
    const nome = RICO_TROCA[e.nome] ?? e.nome;
    if (nome === 'br') {
      if (!e.fechamento) out.push('<br>');
      continue;
    }
    if (e.fechamento) {
      const k = abertas.lastIndexOf(nome);
      if (k < 0) continue;
      while (abertas.length > k) out.push(`</${abertas.pop()}>`);
      continue;
    }
    // Aninhamento fundo não tem uso no editor e só serve para pesar: além de 32 níveis, a etiqueta sai (o texto fica).
    if (abertas.length >= 32) continue;
    abertas.push(nome);
    out.push(`<${nome}${atributosRicos(nome, e.atributos)}>`);
  }
  while (abertas.length) out.push(`</${abertas.pop()}>`);
  const html = out.join('');
  return textoDeHtml(html) ? html : '';
}

/** Texto puro (o que havia antes do editor) como HTML do editor: escapado, linha em branco separa parágrafo, quebra vira `<br>`. */
export function htmlDeTexto(texto: string): string {
  return String(texto ?? '')
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escaparAtributo(p).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/** Texto puro do HTML do editor (o alternativo do e-mail e as colunas de texto): parágrafo e lista separados por linha em branco. */
export function textoDeHtmlRico(html: string): string {
  return textoDeHtml(String(html ?? '').replace(/<\/(p|ul|ol|blockquote)>/gi, '$&<br>'));
}
